import { FeatureFlag } from '@ps/web-core-enums';
import { Size, useFeature } from '@ps/web-core-store';
import { TrackingCategory, TrackingContext } from '@ps/web-shared-analytics';
import type { Pathname } from 'history';
import React, { useContext, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Redirect, Route, Switch, matchPath, useLocation } from 'react-router';

import { ModalContext } from '@swf/components/StarsModal/Context';
import type { ModalContextValue } from '@swf/components/StarsModal/Context';
import NotFound from '@swf/components/StarsModal/NotFound';
import Body from '@swf/components/StarsModal/components/Body';
import { useNativeLoader } from '@swf/core/hooks/useNativeLoader';
import { select } from '@swf/core/store';
import type { Dispatch } from '@swf/core/store';
import AccountVerification from '@swf/verification/AccountVerification/AccountVerification';

import styles from './StarsAuth.scss';
import AuthHeader from './components/AuthHeader/AuthHeader';
import ForceChangePassword from './components/ForceChangePassword/ForceChangePassword';
import LoginEulaForm from './components/LoginEulaForm/LoginEulaForm';
import LoginEulaOptin from './components/LoginEulaOptin/LoginEulaOptin';
import LoginForm from './components/LoginForm/LoginForm';
import LoginSMSForm from './components/LoginSMSForm/LoginSMSForm';
import NemIdForm from './components/NemIdForm/NemIdForm';
import PINForm from './components/PINForm/PINForm';
import PINReset from './components/PINReset/PINReset';
import RSAForm from './components/RSAForm/RSAForm';
import RegistrationBankId from './components/RegistrationBankId/RegistrationBankId';
import RegistrationEula from './components/RegistrationEula/RegistrationEula';
import RegistrationForm from './components/RegistrationForm/RegistrationForm';
import registrationStyles from './components/RegistrationForm/RegistrationForm.scss';
import RegistrationWelcome from './components/RegistrationWelcome/RegistrationWelcome';
import SecurityQuestionsForm from './components/SecurityQuestionsForm/SecurityQuestionsForm';
import SessionTransfer from './components/SessionTransfer/SessionTransfer';

const largeSizeModals = ['/auth/docverify'];

const StarsAuth: React.FC = () => {
  const { pathname } = useLocation();
  const prevPathRef = useRef<Pathname>('');
  const { setStyle } = useContext<ModalContextValue>(ModalContext);
  const isAuthenticated = useSelector(select.session.isAuthenticated);
  const isEmbeddedRoute = useSelector(select.location.isEmbeddedRoute);
  const verificationRMOKCheck = useFeature(FeatureFlag.VerificationRMOKCheck);

  const { auth } = useDispatch<Dispatch>();

  useNativeLoader();

  useEffect(
    () => () => {
      auth.clear();
    },
    [auth]
  );

  useEffect(() => {
    setStyle({ size: largeSizeModals.includes(pathname) ? Size.LARGE : Size.SMALL });
  }, [pathname, setStyle]);

  useEffect(() => {
    const prevPathname = prevPathRef.current;
    const pathChanged = !matchPath(prevPathname, { path: pathname, exact: false });
    const fromHeaderLogin = matchPath(prevPathname, { path: '/', exact: true });
    if (!fromHeaderLogin && pathChanged) {
      auth.clear();
    }

    prevPathRef.current = pathname;
  }, [auth, pathname]);

  return (
    <TrackingContext.Provider value={{ category: TrackingCategory.Auth }}>
      {!isEmbeddedRoute && <AuthHeader />}
      <Body className={styles.body}>
        <Switch>
          <Route path="/auth/registration/eula" component={RegistrationEula} />
          {verificationRMOKCheck && (
            <Route path="/auth/registration/welcome" component={RegistrationWelcome} />
          )}
          {!isAuthenticated && (
            <Route path="/auth/registration/bankid" component={RegistrationBankId} />
          )}
          {!isAuthenticated && <Route path="/auth/registration" component={RegistrationForm} />}
          {isAuthenticated && <Route path="/auth/login/eula/opt-in" component={LoginEulaOptin} />}
          {isAuthenticated && <Route path="/auth/login/eula" component={LoginEulaForm} />}
          {!isAuthenticated && <Route path="/auth/login/nemid" component={NemIdForm} />}
          {!isAuthenticated && <Route path="/auth/login/pin" component={PINForm} />}
          {!isAuthenticated && <Route path="/auth/login/pin-reset" component={PINReset} />}
          <Route path="/auth/login/sms/:resent?" component={LoginSMSForm} />
          {!isAuthenticated && <Route path="/auth/login/rsa" component={RSAForm} />}
          {!isAuthenticated && (
            <Route path="/auth/login/security-questions" component={SecurityQuestionsForm} />
          )}
          <Route path="/auth/force-change-password" component={ForceChangePassword} />
          {(!isAuthenticated || isEmbeddedRoute) && (
            <Route path="/auth/login" component={LoginForm} />
          )}
          {!isAuthenticated && <Route path="/auth/session-transfer" component={SessionTransfer} />}
          {isAuthenticated ? (
            <Route path="/auth/docverify" render={() => <AccountVerification isModal />} />
          ) : (
            <Redirect to={{ pathname: '/auth/login', state: { path: pathname } }} />
          )}
          {!isEmbeddedRoute && <Route component={NotFound} />}
        </Switch>
      </Body>
    </TrackingContext.Provider>
  );
};

export const RegistrationStyles = React.createContext(registrationStyles);

export default StarsAuth;
